import { StepImageDimensions } from '../types';
import { calculatePNGMaskPositioning, loadPNGMask, applyPNGClipping, applyPNGMaskClipping } from './pngProcessor';

/**
 * Utility functions for canvas rendering operations
 * These functions are pure, declarative, and reusable between preview and save handlers
 */

export interface CanvasRenderOptions {
  imageUrl: string;
  rotation: number;
  scale: number;
  flipX: boolean;
  offsetX: number;
  offsetY: number;
  stepImageDimensions: StepImageDimensions | null;
  uploadType: 'photo' | 'engraving';
  processedSrc?: string | null;
  maskSvgBlob?: Blob | null;
  positioning?: { x: number; y: number; scale: number; rotation: number };
}

export interface CanvasSetupOptions {
  stepImageDimensions: StepImageDimensions | null;
}

export interface ImageTransformOptions {
  rotation: number;
  scale: number;
  flipX: boolean;
  offsetX: number;
  offsetY: number;
  uploadType: 'photo' | 'engraving';
  showStarMapSlot?: boolean;
}

export interface MaskRenderOptions {
  maskSvgBlob: Blob;
  positioning?: { x: number; y: number; scale: number; rotation: number };
  actualWidth: number;
  actualHeight: number;
}

/**
 * Log error with consistent formatting
 */
export const logError = (context: string, error: unknown, additionalInfo?: Record<string, unknown>): void => {
  console.error(`❌ ${context}:`, error);
  if (additionalInfo) {
    console.error('Additional info:', additionalInfo);
  }
};

/**
 * Load an image with proper error handling
 */
export const loadImage = async (options: { src: string }): Promise<HTMLImageElement> => {
  const { src } = options;
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
    img.src = src;
  });
};

/**
 * Setup canvas with standardized dimensions
 */
export const setupCanvas = (
  options: CanvasSetupOptions
): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D; actualWidth: number; actualHeight: number } => {
  const { stepImageDimensions } = options;

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Could not get canvas context');
  }

  // Calculate standardized dimensions based on step image
  const standardWidth = 1250;
  const standardHeight = stepImageDimensions
    ? (standardWidth * stepImageDimensions.height) / stepImageDimensions.width
    : standardWidth;

  // Set canvas dimensions to standardized values
  canvas.width = standardWidth;
  canvas.height = standardHeight;

  // Get actual canvas dimensions (browser may round)
  const actualWidth = canvas.width;
  const actualHeight = canvas.height;

  // Fill with white background
  ctx.fillStyle = 'white';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  return { canvas, ctx, actualWidth, actualHeight };
};

/**
 * Calculate image scaling to fit within canvas while maintaining aspect ratio
 */
export const calculateImageScaling = (options: {
  image: HTMLImageElement;
  canvasWidth: number;
  canvasHeight: number;
}): { scaledWidth: number; scaledHeight: number } => {
  const { image, canvasWidth, canvasHeight } = options;
  const imageAspectRatio = image.width / image.height;
  const canvasAspectRatio = canvasWidth / canvasHeight;

  let scaledWidth: number;
  let scaledHeight: number;

  if (imageAspectRatio > canvasAspectRatio) {
    // Image is wider - constrain by width to fit within canvas
    scaledWidth = canvasWidth;
    scaledHeight = canvasWidth / imageAspectRatio;
  } else {
    // Image is taller - constrain by height to fit within canvas
    scaledHeight = canvasHeight;
    scaledWidth = canvasHeight * imageAspectRatio;
  }

  return { scaledWidth, scaledHeight };
};

/**
 * Check if the current image is a star map or location map based on filename
 */
const isStarMapImage = (imageUrl: string | null, selectedImage: File | null): boolean => {
  if (selectedImage?.name === 'star-map.png' || selectedImage?.name === 'location-map.png') {
    return true;
  }
  // Also check if imageUrl contains star map or location map indicators
  if (imageUrl && (imageUrl.includes('star-map') || imageUrl.includes('starMap') || imageUrl.includes('location-map') || imageUrl.includes('locationMap'))) {
    return true;
  }
  return false;
};

/**
 * Apply user transformations to canvas context
 */
export const applyUserTransformations = (ctx: CanvasRenderingContext2D, options: ImageTransformOptions): void => {
  const { rotation, scale, flipX, offsetX, offsetY, uploadType, showStarMapSlot = false } = options;

  ctx.save();

  // Apply engraving filters if needed
  // For star map, always apply blend mode (even if uploadType is 'photo')
  // For regular engravings, apply both contrast and blend mode
  if (uploadType === 'engraving' || showStarMapSlot) {
    // For star map, apply blend mode only (no contrast)
    if (showStarMapSlot) {
      ctx.globalCompositeOperation = 'multiply';
    } else {
      ctx.filter = 'grayscale(100%) contrast(300%)';
      ctx.globalCompositeOperation = 'multiply';
    }
  }

  // Apply transformations
  ctx.rotate((rotation * Math.PI) / 180);
  ctx.scale(scale * (flipX ? -1 : 1), scale);
  ctx.translate(offsetX, offsetY);
};

/**
 * Draw user image with transformations
 */
export const drawUserImage = (options: {
  ctx: CanvasRenderingContext2D;
  image: HTMLImageElement;
  scaledWidth: number;
  scaledHeight: number;
}): void => {
  const { ctx, image, scaledWidth, scaledHeight } = options;
  ctx.drawImage(image, -scaledWidth / 2, -scaledHeight / 2, scaledWidth, scaledHeight);
  ctx.restore();
};

/**
 * Apply mask to the canvas
 */
export const applyMaskToCanvas = async (
  ctx: CanvasRenderingContext2D,
  options: MaskRenderOptions
): Promise<boolean> => {
  const { maskSvgBlob, positioning, actualWidth, actualHeight } = options;

  try {
    // Load the PNG mask image
    const maskImage = await loadPNGMask({ maskSvgBlob });

    // Calculate mask positioning
    const maskPositioning = calculatePNGMaskPositioning({
      containerWidth: actualWidth,
      containerHeight: actualHeight,
      positioning
    });

    // Set up PNG clipping context (but don't apply mask yet)
    const clippingResult = applyPNGClipping({
      ctx,
      maskImage,
      positioning: maskPositioning,
      debugMode: false
    });

    if (clippingResult.success) {
      // Store the mask info for later application
      // The mask will be applied after the user image is drawn
      return true;
    }

    return false;
  } catch (error) {
    logError('Error applying mask to canvas', error, { maskSvgBlob: !!maskSvgBlob });
    return false;
  }
};

/**
 * Apply multiply blend mode to the entire canvas if engraving
 */
export const applyMultiplyBlendMode = (options: {
  ctx: CanvasRenderingContext2D;
  uploadType: 'photo' | 'engraving';
}): void => {
  const { ctx, uploadType } = options;
  if (uploadType === 'engraving') {
    ctx.save();
    ctx.globalCompositeOperation = 'multiply';
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    ctx.restore();
  }
};

/**
 * Ensure white background is maintained in areas outside the mask
 */
export const maintainWhiteBackground = (options: { ctx: CanvasRenderingContext2D }): void => {
  const { ctx } = options;
  ctx.save();
  ctx.globalCompositeOperation = 'destination-over';
  ctx.fillStyle = 'white';
  ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
  ctx.restore();
};

/**
 * Render user image with mask (main rendering logic)
 */
export const renderUserImageWithMask = async (
  ctx: CanvasRenderingContext2D,
  userImage: HTMLImageElement,
  scaledWidth: number,
  scaledHeight: number,
  options: ImageTransformOptions & MaskRenderOptions
): Promise<void> => {
  const {
    maskSvgBlob,
    positioning,
    actualWidth,
    actualHeight,
    uploadType,
    rotation,
    scale,
    flipX,
    offsetX,
    offsetY,
    showStarMapSlot = false
  } = options;

  try {
    if (maskSvgBlob) {
      // Load the PNG mask image
      const maskImage = await loadPNGMask({ maskSvgBlob });

      // Calculate mask positioning
      const maskPositioning = calculatePNGMaskPositioning({
        containerWidth: actualWidth,
        containerHeight: actualHeight,
        positioning
      });

      // Set up PNG clipping context (but don't apply mask yet)
      const clippingResult = applyPNGClipping({
        ctx,
        maskImage,
        positioning: maskPositioning,
        debugMode: false
      });

      if (clippingResult.success) {
        // Apply user transformations
        ctx.save();
        // Move to the mask center position first
        ctx.translate(maskPositioning.maskCenterX, maskPositioning.maskCenterY);
        // Then apply user transformations
        ctx.rotate((rotation * Math.PI) / 180);
        ctx.scale(scale * (flipX ? -1 : 1), scale);
        ctx.translate(offsetX, offsetY);

        // Apply engraving filters if needed
        // For star map, always apply blend mode (even if uploadType is 'photo')
        if (uploadType === 'engraving' || showStarMapSlot) {
          // For star map, apply blend mode only (no contrast)
          if (showStarMapSlot) {
            ctx.globalCompositeOperation = 'multiply';
          } else {
            ctx.filter = 'grayscale(100%) contrast(300%)';
            ctx.globalCompositeOperation = 'multiply';
          }
        }

        // Draw the user image
        ctx.drawImage(userImage, -scaledWidth / 2, -scaledHeight / 2, scaledWidth, scaledHeight);

        // Restore user transformations
        ctx.restore();

        // NOW apply the mask to clip the user's image
        applyPNGMaskClipping({ ctx, maskImage, positioning: maskPositioning });

        // Apply multiply blend mode to the masked result if engraving or star map
        if (uploadType === 'engraving' || showStarMapSlot) {
          ctx.save();
          ctx.globalCompositeOperation = 'multiply';
          ctx.fillStyle = 'white';
          ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
          ctx.restore();
        }

        // Ensure white background is maintained in areas outside the mask
        if (uploadType === 'engraving' || showStarMapSlot) {
          ctx.save();
          ctx.globalCompositeOperation = 'destination-over';
          ctx.fillStyle = 'white';
          ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
          ctx.restore();
        }
      } else {
        // Fallback: draw user image without mask
        ctx.save();
        ctx.translate(ctx.canvas.width / 2, ctx.canvas.height / 2);
        ctx.rotate((rotation * Math.PI) / 180);
        ctx.scale(scale * (flipX ? -1 : 1), scale);
        ctx.translate(offsetX, offsetY);

        if (uploadType === 'engraving') {
          // For star map, apply blend mode only (no contrast)
          if (showStarMapSlot) {
            ctx.globalCompositeOperation = 'multiply';
          } else {
            ctx.filter = 'grayscale(100%) contrast(300%)';
            ctx.globalCompositeOperation = 'multiply';
          }
        }

        ctx.drawImage(userImage, -scaledWidth / 2, -scaledHeight / 2, scaledWidth, scaledHeight);
        ctx.restore();
      }
    } else {
      // No mask: draw user image without mask
      ctx.save();
      ctx.translate(ctx.canvas.width / 2, ctx.canvas.height / 2);
      ctx.rotate((rotation * Math.PI) / 180);
      ctx.scale(scale * (flipX ? -1 : 1), scale);
      ctx.translate(offsetX, offsetY);

      if (uploadType === 'engraving') {
        // For star map, apply blend mode only (no contrast)
        if (showStarMapSlot) {
          ctx.globalCompositeOperation = 'multiply';
        } else {
          ctx.filter = 'grayscale(100%) contrast(300%)';
          ctx.globalCompositeOperation = 'multiply';
        }
      }

      ctx.drawImage(userImage, -scaledWidth / 2, -scaledHeight / 2, scaledWidth, scaledHeight);
      ctx.restore();
    }
  } catch (error) {
    logError('Error rendering user image with mask', error, {
      hasMask: !!maskSvgBlob,
      uploadType,
      imageSize: { width: userImage.width, height: userImage.height }
    });
    throw error;
  }
};

/**
 * Render user image without mask (fallback rendering)
 */
export const renderUserImageWithoutMask = (
  ctx: CanvasRenderingContext2D,
  userImage: HTMLImageElement,
  scaledWidth: number,
  scaledHeight: number,
  options: ImageTransformOptions
): void => {
  try {
    // Apply user transformations
    applyUserTransformations(ctx, options);

    // Draw the user image
    drawUserImage({ ctx, image: userImage, scaledWidth, scaledHeight });
  } catch (error) {
    logError('Error rendering user image without mask', error, {
      uploadType: options.uploadType,
      imageSize: { width: userImage.width, height: userImage.height }
    });
    throw error;
  }
};
